import { Segment, Header, Card, Loader, Image } from "semantic-ui-react";
import { TransitionGroup } from "components";
import { PoolShapeSelect } from "components/project/PoolShapeSelect";
import { type Project, type Resource, ProjectModel } from "models";
import type { ModelInstance } from "models/Model";
import { OrderType, PoolShapeType } from "models/Order";
import * as PoolShapeSvg from "models/PoolShapeImages";
import { PoolShapeOptions } from "models/PoolShapeOptions";
import { useRef, useCallback, useState, useEffect } from "react";
import type React from "react";
import ImageGallery from "react-image-gallery";
import { USER_MSG, getTranslatedUserMsg } from "strings";
import "components/project/PoolShapeSelect.scss";
import "./ProjectMeasurement.scss";
import Cross from "res/img/generate_drawing.svg?react";
import { useT } from "i18n";
import { useUI, useAuth, hasSuperAccess, useFeatureFlags } from "services";
import { ProjectService } from ".";
import { checkFeatureAuthorization, Features } from "../../utils";

interface ProjectMeasurementProps {
  project: Project;
  generateDxf: (project: Project) => void;
  isGeneratingDxf: boolean;
  updateProject: React.Dispatch<React.SetStateAction<ModelInstance<Project>>>;
}

export default function ProjectMeasurement({
  project,
  generateDxf,
  isGeneratingDxf,
  updateProject,
}: ProjectMeasurementProps): React.ReactElement {
  const t = useT();
  const { user } = useAuth();
  const [coverDxfObject, setCoverDxfObject] = useState<Resource>();
  const [linerDxfObject, setLinerDxfObject] = useState<Resource>();
  const [coverImageReference, setCoverImageReference] = useState<string>();
  const [linerImageReference, setLinerImageReference] = useState<string>();
  const [coverBase64Img, setCoverBase64Img] = useState<string>();
  const [linerBase64Img, setLinerBase64Img] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showCoverGallery, setCoverShowGallery] = useState(false);
  const [showLinerGallery, setLinerShowGallery] = useState(false);
  const { showError, showSuccess } = useUI();
  const coverGallery = useRef<ImageGallery | null>(null);
  const linerGallery = useRef<ImageGallery | null>(null);
  const flags = useFeatureFlags();
  const showLinerUi = flags.showLinerUi;
  const hasDownloadAccess = checkFeatureAuthorization(Features.DownloadDxf, user?.role);

  const DownloadAllowed = ({
    isDownloadAllowed,
    href,
    downloadUrl,
    children,
  }: {
    isDownloadAllowed: boolean;
    href: string | undefined;
    downloadUrl: string;
    children: React.ReactNode;
  }) => {
    return isDownloadAllowed ? (
      <a href={href} rel={"noreferrer"} download={downloadUrl}>
        {children}
      </a>
    ) : (
      <a>{children}</a>
    );
  };

  const getPoolShapeName = () => {
    const selectedShape = PoolShapeOptions.filter((el) => {
      return (
        el.value === project?.poolDetails?.poolShape?.baseName &&
        project?.poolDetails?.poolShape?.orientation?.includes(el.orientation)
      );
    });
    return selectedShape[0]?.text;
  };

  const getPoolShapeObj = (shapeName: string) => {
    const shape = shapeName;
    const selectedShape = PoolShapeOptions.filter((el) => {
      return el.text === shape;
    });
    return selectedShape[0];
  };
  const [poolShape, setPoolShape] = useState(getPoolShapeName());
  const [poolShapeObj, setPoolShapeObj] = useState(getPoolShapeObj(getPoolShapeName()));
  const getDxfResource = useCallback(
    (cover: boolean) => {
      if (cover) {
        const resources = project?.resources?.filter(
          (resource) =>
            resource.type.toString() === "dxf" &&
            resource.status === "uploaded" &&
            !resource.name.includes("Liner"),
        );
        if (resources && resources?.length > 0) {
          return resources[0];
        }
      } else {
        const resources = project?.resources?.filter(
          (resource) =>
            resource.type.toString() === "dxf" &&
            resource.status === "uploaded" &&
            resource.name.includes("Liner"),
        );
        if (resources && resources?.length > 0) {
          return resources[0];
        }
      }
      return undefined;
    },
    [project],
  );

  useEffect(() => {
    setCoverDxfObject((getDxfResource(true) || {}) as Resource);
    const imgResource = project?.resources?.filter(
      (resource) =>
        resource.type.toString() === "dxf image" &&
        resource.status === "uploaded" &&
        !resource.name.includes("Liner"),
    );
    if (imgResource) {
      if (imgResource && imgResource.length > 0) {
        setCoverImageReference(imgResource[0].url);
        if (
          imgResource[0]?.id &&
          checkFeatureAuthorization(Features.GetBase64Image, user?.role)
        ) {
          getBase64Image(imgResource[0].id, true);
        }
      }
    }
  }, [getDxfResource, setCoverImageReference, project]);

  useEffect(() => {
    if (showLinerUi) {
      setLinerDxfObject((getDxfResource(false) || {}) as Resource);
    }
    const imgResource = project?.resources?.filter(
      (resource) =>
        resource.type.toString() === "dxf image" && resource.name.includes("Liner"),
    );
    if (imgResource && imgResource?.length > 0) {
      if (showLinerUi) {
        setLinerImageReference(imgResource[0].url);
      }
      if (
        imgResource[0]?.id &&
        checkFeatureAuthorization(Features.GetBase64Image, user?.role)
      ) {
        getBase64Image(imgResource[0].id, false);
      }
    }
  }, [getDxfResource, setLinerImageReference, project]);

  const getBase64Image = async (resourceId: string, cover: boolean) => {
    const base46Data = await ProjectService.getBase64Image(resourceId);
    if (cover) {
      setCoverBase64Img(base46Data);
    } else {
      if (showLinerUi) {
        setLinerBase64Img(base46Data);
      }
    }
  };

  const onError = useCallback(() => {
    showError();
  }, [showError]);

  const onCancel = () => {
    const shapeName = getPoolShapeName();
    setPoolShape(shapeName);
    setPoolShapeObj(getPoolShapeObj(shapeName));
    setIsOpen(false);
  };

  const saveProject = async () => {
    setLoading(true);
    delete project.dealerName;
    setPoolShapeObj(getPoolShapeObj(poolShape));
    const selectedShape = PoolShapeOptions.filter((el) => {
      return el.text === poolShape;
    });
    const newProject = {
      ...project,
      poolDetails: {
        ...project?.poolDetails,
        poolShape: poolShape
          ? {
              ...project?.poolDetails?.poolShape,
              baseName: selectedShape[0].value,
              baseCode: selectedShape[0].code,
              orientation: selectedShape[0].orientation,
              geometric:
                selectedShape[0].type === PoolShapeType.Geometric
                  ? selectedShape[0].type
                  : null,
              freeform:
                selectedShape[0].type === PoolShapeType.Freeform
                  ? selectedShape[0].type
                  : null,
            }
          : undefined,
        poolShapeType: selectedShape[0]?.type,
      },
    };
    const data = { ...newProject };
    delete data.features;
    delete data.resources;
    delete data.scans;
    delete data.orders;

    await ProjectService.set(data, "PATCH", false)
      .then(() => {
        project.resources && project.resources?.length > 0
          ? null
          : showSuccess(getTranslatedUserMsg(t, "SUCCESS_GENERIC"));
      }, onError)
      .catch(async (error) => {
        showError({
          title: t(
            "userMsg.errorSavingProject.title",
            USER_MSG.ERROR_SAVING_PROJECT.title,
          ),
          msg: error.message,
        });
      })
      .finally(() => {
        setLoading(false);
      });
    updateProject(ProjectModel({ ...newProject }));
    return newProject;
  };

  const onSubmit = async () => {
    const newProject = await saveProject();
    setIsOpen(false);
    generateDxf(newProject);
  };

  const handleOpenModal = (): void => {
    setIsOpen(true);
  };

  return (
    <TransitionGroup isVisible={!!coverDxfObject}>
      <Segment className={"pad"}>
        <Header size={"large"}>
          {t("section.projectMeasurement.projectMeasurement", "Project Measurement")}
        </Header>
        <div className={"project-measurement-card-group"}>
          <Card className={"select-pool-card"}>
            <Card.Header className={"shape-card-header"}>
              {poolShapeObj?.text ??
                t("section.projectMeasurement.poolShape", "Pool Shape")}
            </Card.Header>
            <div
              className={"shape-card-container"}
              onClick={
                checkFeatureAuthorization(Features.SelectPoolShape, user?.role)
                  ? handleOpenModal
                  : undefined
              }
            >
              <>
                {poolShapeObj?.image?.src ? (
                  <Image
                    className={"shape-card-image"}
                    src={poolShapeObj?.image?.src}
                  />
                ) : (
                  <Cross className={"shape-card-image-cross"} />
                )}
                {checkFeatureAuthorization(Features.SelectPoolShape, user?.role) && (
                  <div className={"select-pool-shape"}>
                    {t("section.projects.generateDrawing", "Generate Drawing")}
                  </div>
                )}
              </>
            </div>
            <PoolShapeSelect
              value={poolShape}
              onChange={setPoolShape}
              options={PoolShapeOptions}
              isOpen={isOpen}
              poolShape={getPoolShapeName()}
              onCancel={onCancel}
              onSubmit={onSubmit}
              loading={loading}
            />
          </Card>
          <div className={"feature-card-group"}>
            <div className={"cover-card-group"}>
              {coverDxfObject?.id && (
                <Header size={"medium"} className={"feature-header"}>
                  {t("component.orders.cover", "Cover")}
                </Header>
              )}
              <Card>
                <Card.Header>
                  {coverDxfObject?.id && hasDownloadAccess && (
                    <DownloadAllowed
                      isDownloadAllowed={hasDownloadAccess}
                      href={coverDxfObject.url}
                      downloadUrl={`${coverDxfObject.id}-${
                        coverDxfObject.updateTime?.split("T")[0]
                      }`}
                    >
                      {t("section.projects.downloadCoverDxf", "Download Cover DXF")}
                    </DownloadAllowed>
                  )}
                </Card.Header>
                {!loading && !isGeneratingDxf ? (
                  <Card.Content>
                    <Card.Meta>
                      {t("section.projectMeasurement.takenOn", "Taken On")}
                      <Card.Description>
                        {coverDxfObject?.updateTime?.split("T")[0] ||
                          t(
                            "section.projectMeasurement.noDateCaptured",
                            "No Date Captured",
                          )}
                      </Card.Description>
                      {hasSuperAccess(user) && project?.poolDetails?.measurements && (
                        <button
                          className={"ui primary button mtop"}
                          onClick={() => {
                            generateDxf(project);
                          }}
                        >
                          {coverDxfObject?.updateTime
                            ? t("section.projectMeasurement.regenerate", "Regenerate")
                            : t("section.projectMeasurement.generate", "Generate")}
                        </button>
                      )}
                    </Card.Meta>
                  </Card.Content>
                ) : (
                  <Loader
                    active
                    size={"small"}
                    content={t("section.projectMeasurement.loading", "Loading")}
                  />
                )}
              </Card>
              <Card className={"drawing-for-file-container"}>
                {coverImageReference && (
                  <Card.Header className={"drawing-for-file-header"}>
                    <a
                      href={`data:image/png;base64,${coverBase64Img}`}
                      rel={"noreferrer"}
                      download={`${project?.name}-drawing-for-file-cover`}
                    >
                      {t(
                        "section.projects.downloadCoverDrawingForFile",
                        "Download Cover Drawing for File",
                      )}
                    </a>
                  </Card.Header>
                )}
                {!loading && !isGeneratingDxf ? (
                  <Card.Content className={"drawing-for-file-image"}>
                    {coverImageReference ? (
                      <img
                        className={"pointer fullh fullw"}
                        src={coverImageReference}
                        alt={coverImageReference}
                        onClick={() => {
                          coverGallery.current?.slideToIndex(1);
                          coverGallery.current?.fullScreen();
                        }}
                      />
                    ) : project?.poolDetails?.measurements ? (
                      <div
                        style={{
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <div style={{ textAlign: "center" }}>
                          <i
                            style={{ fontSize: "5em", color: "#db2828" }}
                            className={"exclamation icon"}
                          />
                          <p>
                            {t(
                              "section.projects.coverDrawingError",
                              "An error occurred when generating the Cover Drawing For File. Please contact your administrator",
                            )}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <Card.Meta>
                        <Card.Description>
                          {t(
                            "section.projectMeasurement.noImageCaptured",
                            "No Image Captured",
                          )}
                        </Card.Description>
                      </Card.Meta>
                    )}
                    <div
                      style={{
                        visibility: showCoverGallery ? "visible" : "hidden",
                      }}
                    >
                      <ImageGallery
                        ref={coverGallery}
                        items={[
                          {
                            original: coverImageReference || "",
                            thumbnail: coverImageReference,
                          },
                        ]}
                        onScreenChange={setCoverShowGallery}
                        useBrowserFullscreen={false}
                        additionalClass={"hidden" + showCoverGallery ? "" : "show"}
                        showPlayButton={false}
                        showThumbnails={false}
                        showFullscreenButton={showCoverGallery}
                      />
                    </div>
                  </Card.Content>
                ) : (
                  <Loader
                    active
                    size={"small"}
                    content={t("section.projectMeasurement.loading", "Loading")}
                  />
                )}
              </Card>
            </div>
            {showLinerUi && linerDxfObject?.id && <div className={"vertical-line"} />}
            <div className={"liner-card-group"}>
              {showLinerUi && linerDxfObject?.id && (
                <>
                  <Header size={"medium"} className={"feature-header"}>
                    {t("component.orders.liner", "Liner")}
                  </Header>
                  <Card>
                    <Card.Header>
                      {linerDxfObject?.id && hasDownloadAccess && (
                        <DownloadAllowed
                          isDownloadAllowed={hasDownloadAccess}
                          href={linerDxfObject.url}
                          downloadUrl={`${linerDxfObject.id}-${
                            linerDxfObject.updateTime?.split("T")[0]
                          }`}
                        >
                          {t("section.projects.downloadLinerDxf", "Download Liner DXF")}
                        </DownloadAllowed>
                      )}
                    </Card.Header>
                    {!loading && !isGeneratingDxf ? (
                      <Card.Content>
                        <Card.Meta>
                          {t("section.projectMeasurement.takenOn", "Taken On")}
                          <Card.Description>
                            {linerDxfObject?.updateTime?.split("T")[0] ||
                              t(
                                "section.projectMeasurement.noDateCaptured",
                                "No Date Captured",
                              )}
                          </Card.Description>
                          {hasSuperAccess(user) &&
                            project?.poolDetails?.measurements && (
                              <button
                                className={"ui primary button mtop"}
                                onClick={() => {
                                  generateDxf(project);
                                }}
                              >
                                {linerDxfObject?.updateTime
                                  ? t(
                                      "section.projectMeasurement.regenerate",
                                      "Regenerate",
                                    )
                                  : t(
                                      "section.projectMeasurement.generate",
                                      "Generate",
                                    )}
                              </button>
                            )}
                        </Card.Meta>
                      </Card.Content>
                    ) : (
                      <Loader
                        active
                        size={"small"}
                        content={t("section.projectMeasurement.loading", "Loading")}
                      />
                    )}
                  </Card>
                  <Card className={"drawing-for-file-container"}>
                    {linerImageReference && (
                      <Card.Header className={"drawing-for-file-header"}>
                        <a
                          href={`data:image/png;base64,${linerBase64Img}`}
                          rel={"noreferrer"}
                          download={`${project?.name}-drawing-for-file-liner`}
                        >
                          {t(
                            "section.projects.downloadLinerDrawingForFile",
                            "Download Liner Drawing for File",
                          )}
                        </a>
                      </Card.Header>
                    )}
                    {!loading && !isGeneratingDxf ? (
                      <Card.Content className={"drawing-for-file-image"}>
                        {linerImageReference ? (
                          <img
                            className={"pointer fullh fullw"}
                            src={linerImageReference}
                            alt={linerImageReference}
                            onClick={() => {
                              linerGallery.current?.slideToIndex(1);
                              linerGallery.current?.fullScreen();
                            }}
                          />
                        ) : project?.poolDetails?.measurements ? (
                          <div
                            style={{
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <div style={{ textAlign: "center" }}>
                              <i
                                style={{ fontSize: "5em", color: "#db2828" }}
                                className={"exclamation icon"}
                              />
                              <p>
                                {t(
                                  "section.projects.linerDrawingError",
                                  "An error occurred when generating the Drawing For File. Please contact your administrator",
                                )}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <Card.Meta>
                            <Card.Description>
                              {t(
                                "section.projectMeasurement.noImageCaptured",
                                "No Image Captured",
                              )}
                            </Card.Description>
                          </Card.Meta>
                        )}
                        <div
                          style={{
                            visibility: showLinerGallery ? "visible" : "hidden",
                          }}
                        >
                          <ImageGallery
                            ref={linerGallery}
                            items={[
                              {
                                original: linerImageReference || "",
                                thumbnail: linerImageReference,
                              },
                            ]}
                            onScreenChange={setLinerShowGallery}
                            useBrowserFullscreen={false}
                            additionalClass={"hidden" + showLinerGallery ? "" : "show"}
                            showPlayButton={false}
                            showThumbnails={false}
                            showFullscreenButton={showLinerGallery}
                          />
                        </div>
                      </Card.Content>
                    ) : (
                      <Loader
                        active
                        size={"small"}
                        content={t("section.projectMeasurement.loading", "Loading")}
                      />
                    )}
                  </Card>
                </>
              )}
            </div>
          </div>
        </div>
      </Segment>
    </TransitionGroup>
  );
}
